import { useEffect, useMemo, useState } from 'react';
import { Button, message, Spin, Table } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { DownloadOutlined } from '@ant-design/icons';
import { getAnalysisDetails, policyAnalysisDownload } from '../../../infrastructure/apiClient';
import type { PolicyAnalysisDTO, AnalysisDiscrepancyDTO, BuildingBlockSummaryDTO } from '../../../infrastructure/models';
import { buildFilterOptions, getCheckboxFilterProps, type CheckboxFilterOption } from '../../../components/Table/columnFilterUtils';
import { POLICY_CONFIGURATION_BUILDING_BLOCKS } from '../constants/buildingBlocks';
import styles from './AnalysisDetails.module.scss';

interface AnalysisDetailsProps {
	analysis?: PolicyAnalysisDTO | null;
	onBack: () => void;
	tenantId?: string;
}

const BUILDING_BLOCK_LABEL_BY_CODE = POLICY_CONFIGURATION_BUILDING_BLOCKS.reduce<Record<string, string>>((acc, block) => {
	acc[block.code] = block.label;
	return acc;
}, {});

type FilterKey = 'buildingBlock' | 'componentBlock' | 'impactedType' | 'impactedBlock';

const filterGetters: Record<FilterKey, (record: AnalysisDiscrepancyDTO) => string | undefined> = {
	buildingBlock: (record) => record.buildingBlockName ?? undefined,
	componentBlock: (record) => record.componentBuildingBlock ?? undefined,
	impactedType: (record) => record.impactedBlockType ?? undefined,
	impactedBlock: (record) => record.impactedBlock ?? undefined,
};

const EMPTY_DISCREPANCIES: AnalysisDiscrepancyDTO[] = [];

const getConfigurationTypeLabel = (code?: string | null) => {
	if (!code) {
		return '—';
	}
	return BUILDING_BLOCK_LABEL_BY_CODE[code] ?? code;
};

const formatRegionLabel = (region?: { continent?: string | null; country?: string | null; state?: string | null } | null) => {
	if (!region) {
		return '—';
	}
	const parts = [region.continent, region.country, region.state].filter((part): part is string => Boolean(part && part.trim()));
	return parts.length ? parts.join(' / ') : '—';
};

const AnalysisDetails = ({ analysis, onBack, tenantId }: AnalysisDetailsProps) => {
	const [details, setDetails] = useState<PolicyAnalysisDTO | null>(analysis ?? null);
	const [loading, setLoading] = useState(false);
	const [csvDownloading, setCsvDownloading] = useState(false);
	const [buildingBlockFilters, setBuildingBlockFilters] = useState<string[]>([]);
	const [componentBlockFilters, setComponentBlockFilters] = useState<string[]>([]);
	const [impactedTypeFilters, setImpactedTypeFilters] = useState<string[]>([]);
	const [impactedBlockFilters, setImpactedBlockFilters] = useState<string[]>([]);

	const filtersByKey: Record<FilterKey, string[]> = {
		buildingBlock: buildingBlockFilters,
		componentBlock: componentBlockFilters,
		impactedType: impactedTypeFilters,
		impactedBlock: impactedBlockFilters,
	};

	useEffect(() => {
		const effectiveAnalysisId = analysis?.comparisonId;
		if (!effectiveAnalysisId) {
			setDetails(null);
			return;
		}

		const controller = new AbortController();

		const loadDetails = async () => {
			const tenantToUse = tenantId ?? analysis?.tenantId;
			if (!tenantToUse) {
				message.warning('Select a tenant to view analysis details.');
				setDetails(null);
				return;
			}

			try {
				setLoading(true);
				setDetails(null);
				const response = await getAnalysisDetails(tenantToUse, effectiveAnalysisId, controller.signal);
				setDetails(response);
			} catch (err) {
				if (!controller.signal.aborted) {
					console.error('Failed to load analysis details', err);
					message.error('Unable to load analysis details.');
					setDetails(null);
				}
			} finally {
				if (!controller.signal.aborted) {
					setLoading(false);
				}
			}
		};

		loadDetails();

		return () => controller.abort();
	}, [analysis?.comparisonId, analysis?.tenantId, tenantId]);
	

	const regionSource = details ?? analysis ?? null;
	const regionLabel = useMemo(() => formatRegionLabel(regionSource), [regionSource]);
	const discrepancies = details?.discrepancies ?? EMPTY_DISCREPANCIES;
	const hasDiscrepancies = discrepancies.length > 0;

	const recordMatchesFilters = (record: AnalysisDiscrepancyDTO, keys: FilterKey[]) =>
		keys.every((key) => {
			const activeFilters = filtersByKey[key];
			if (!activeFilters.length) {
				return true;
			}
			const value = filterGetters[key](record);
			return Boolean(value && activeFilters.includes(value));
		});

	const getScopedRecords = (excludeKey?: FilterKey) => {
		const keysToCheck = (Object.keys(filterGetters) as FilterKey[]).filter((key) => key !== excludeKey);
		return discrepancies.filter((record) => recordMatchesFilters(record, keysToCheck));
	};

	const buildingBlockOptionUniverse = useMemo<CheckboxFilterOption[]>(
		() => buildFilterOptions(discrepancies, filterGetters.buildingBlock),
		[discrepancies],
	);

	const componentBlockOptionUniverse = useMemo<CheckboxFilterOption[]>(
		() => buildFilterOptions(discrepancies, filterGetters.componentBlock),
		[discrepancies],
	);

	const impactedTypeOptionUniverse = useMemo<CheckboxFilterOption[]>(
		() => buildFilterOptions(discrepancies, filterGetters.impactedType),
		[discrepancies],
	);

	const impactedBlockOptionUniverse = useMemo<CheckboxFilterOption[]>(
		() => buildFilterOptions(discrepancies, filterGetters.impactedBlock),
		[discrepancies],
	);

	const buildingBlockFilterOptions = useMemo<CheckboxFilterOption[]>(
		() => {
			const scopedRecords = getScopedRecords('buildingBlock');
			return buildFilterOptions(scopedRecords, filterGetters.buildingBlock);
		},
		[discrepancies, componentBlockFilters, impactedTypeFilters, impactedBlockFilters],
	);

	const componentBlockFilterOptions = useMemo<CheckboxFilterOption[]>(
		() => {
			const scopedRecords = getScopedRecords('componentBlock');
			return buildFilterOptions(scopedRecords, filterGetters.componentBlock);
		},
		[discrepancies, buildingBlockFilters, impactedTypeFilters, impactedBlockFilters],
	);

	const impactedTypeFilterOptions = useMemo<CheckboxFilterOption[]>(
		() => {
			const scopedRecords = getScopedRecords('impactedType');
			return buildFilterOptions(scopedRecords, filterGetters.impactedType);
		},
		[discrepancies, buildingBlockFilters, componentBlockFilters, impactedBlockFilters],
	);

	const impactedBlockFilterOptions = useMemo<CheckboxFilterOption[]>(
		() => {
			const scopedRecords = getScopedRecords('impactedBlock');
			return buildFilterOptions(scopedRecords, filterGetters.impactedBlock);
		},
		[discrepancies, buildingBlockFilters, componentBlockFilters, impactedTypeFilters],
	);

	useEffect(() => {
		setBuildingBlockFilters((prev) =>
			prev.filter((value) => buildingBlockOptionUniverse.some((option) => option.value === value)),
		);
	}, [buildingBlockOptionUniverse]);

	useEffect(() => {
		setComponentBlockFilters((prev) =>
			prev.filter((value) => componentBlockOptionUniverse.some((option) => option.value === value)),
		);
	}, [componentBlockOptionUniverse]);

	useEffect(() => {
		setImpactedTypeFilters((prev) =>
			prev.filter((value) => impactedTypeOptionUniverse.some((option) => option.value === value)),
		);
	}, [impactedTypeOptionUniverse]);

	useEffect(() => {
		setImpactedBlockFilters((prev) =>
			prev.filter((value) => impactedBlockOptionUniverse.some((option) => option.value === value)),
		);
	}, [impactedBlockOptionUniverse]);

	const discrepancyColumns = useMemo<ColumnsType<AnalysisDiscrepancyDTO>>(
		() => [
			{
				title: 'Building Block',
				dataIndex: 'buildingBlockName',
				key: 'buildingBlockName',
				ellipsis: true,
				...getCheckboxFilterProps(
					buildingBlockFilters,
					setBuildingBlockFilters,
					buildingBlockFilterOptions,
					(record) => record.buildingBlockName ?? undefined,
				),
			},
			{
				title: 'Component Block',
				dataIndex: 'componentBuildingBlock',
				key: 'componentBuildingBlock',
				ellipsis: true,
				...getCheckboxFilterProps(
					componentBlockFilters,
					setComponentBlockFilters,
					componentBlockFilterOptions,
					(record) => record.componentBuildingBlock ?? undefined,
				),
			},
			{
				title: 'Impacted Type',
				dataIndex: 'impactedBlockType',
				key: 'impactedBlockType',
				ellipsis: true,
				...getCheckboxFilterProps(
					impactedTypeFilters,
					setImpactedTypeFilters,
					impactedTypeFilterOptions,
					(record) => record.impactedBlockType ?? undefined,
				),
			},
			{
				title: 'Impacted Block',
				dataIndex: 'impactedBlock',
				key: 'impactedBlock',
				ellipsis: true,
				...getCheckboxFilterProps(
					impactedBlockFilters,
					setImpactedBlockFilters,
					impactedBlockFilterOptions,
					(record) => record.impactedBlock ?? undefined,
				),
			},
			{
				title: 'Comments',
				dataIndex: 'discrepancyComments',
				key: 'discrepancyComments',
			},
		],
		[
			buildingBlockFilterOptions,
			buildingBlockFilters,
			componentBlockFilterOptions,
			componentBlockFilters,
			impactedTypeFilterOptions,
			impactedTypeFilters,
			impactedBlockFilterOptions,
			impactedBlockFilters,
		],
	);

	const meta = useMemo(() => {
		if (!analysis && !details) {
			return [];
		}

		const statusSource = details ?? analysis;
		const configurationTypeValue = getConfigurationTypeLabel(details?.configurationType ?? analysis?.configurationType);
		return [
			{ label: 'Configuration Type', value: configurationTypeValue },
			{ label: 'Sub-Type', value: analysis?.configurationSubtype ?? '—' },
			{ label: 'Region', value: regionLabel },
			{ label: 'Status', value: statusSource?.status ?? '—' },
			{ label: 'Requested By', value: statusSource?.createdBy ?? '—' },
		];
	}, [analysis, details, regionLabel]);

	const downloadCsv = async (url: string, fileName: string) => {
		try {
			setCsvDownloading(true);
			policyAnalysisDownload(url, fileName);
		} catch (error) {
			console.error('Unable to download analysis CSV', error);
			message.error('Unable to download analysis CSV.');
		} finally {
			setCsvDownloading(false);
		}
	};

	const summaryText = useMemo(() => {
		if (!analysis && !details) {
			return 'Select a policy analysis to view its discrepancies.';
		}

		if (!details) {
			return 'Analysis is processing. Please check back shortly.';
		}

		if (hasDiscrepancies) {
			return `Detected ${discrepancies.length} ${discrepancies.length === 1 ? 'discrepancy' : 'discrepancies'} for ${regionLabel}.`;
		}

		return `No discrepancies were detected for ${regionLabel}.`;
	}, [analysis, details, discrepancies.length, hasDiscrepancies, regionLabel]);

	const downloadAvailable = Boolean(details?.downloadCsvUrl || details?.downloadExcelUrl);

	const componentBlockSummary = useMemo(() => {
		const countMap = new Map<string, number>();
		discrepancies.forEach((record) => {
			const componentBlock = record.componentBuildingBlock;
			if (componentBlock && componentBlock.trim()) {
				countMap.set(componentBlock, (countMap.get(componentBlock) || 0) + 1);
			}
		});

		return Array.from(countMap.entries())
			.map(([componentBlock, count]) => ({ componentBlock, count }))
			.sort((a, b) => b.count - a.count);
	}, [discrepancies]);

	const sortedDiscrepancies = useMemo(() => {
		const countMap = new Map<string, number>();
		discrepancies.forEach((record) => {
			const componentBlock = record.componentBuildingBlock || '';
			countMap.set(componentBlock, (countMap.get(componentBlock) || 0) + 1);
		});

		return [...discrepancies].sort((a, b) => {
			const componentA = a.componentBuildingBlock || '';
			const componentB = b.componentBuildingBlock || '';
			
			const countA = countMap.get(componentA) || 0;
			const countB = countMap.get(componentB) || 0;
			
			if (countA !== countB) {
				return countB - countA;
			}
			
			return componentA.localeCompare(componentB);
		});
	}, [discrepancies]);

	const totalSitesEvaluated = useMemo(
    () => (details?.buildingBlockSummaries ?? []).reduce((sum, s) => sum + s.totalSitesEvaluated, 0),
    [details?.buildingBlockSummaries],
);

	const summaryColumns = useMemo<ColumnsType<{ componentBlock: string; count: number }>>(
    () => [
        {
            title: 'Component Block',
            dataIndex: 'componentBlock',
            key: 'componentBlock',
        },
        {
            title: 'Number of discrepancies',
            dataIndex: 'count',
            key: 'count',
        },
        {
            title: 'Discrepancy Percentage',
            key: 'percentageOfTotal',
            render: (_: unknown, record: { count: number }) =>
                totalSitesEvaluated > 0
                    ? `${((record.count / totalSitesEvaluated) * 100).toFixed(1)}%`
                    : '0%',
        },
    ],
    [totalSitesEvaluated],
);

	const buildingBlockSummaryColumns = useMemo<ColumnsType<BuildingBlockSummaryDTO>>(
		() => [
			{
				title: 'Building Block',
				dataIndex: 'buildingBlockName',
				key: 'buildingBlockName',
			},
			{
				title: 'Total Sites',
				dataIndex: 'totalSitesEvaluated',
				key: 'totalSitesEvaluated',
			},
			{
				title: 'Has Clear Majority',
				dataIndex: 'hasClearMajority',
				key: 'hasClearMajority',
				render: (_: boolean, record: BuildingBlockSummaryDTO) =>
					record.hasClearMajority ? 'Yes' : 'No',
			},
			{
				title: 'Standard Component',
				dataIndex: 'majorityQualifier',
				key: 'majorityQualifier',
				render: (_: number, record: BuildingBlockSummaryDTO) =>
					record.hasClearMajority ? record.majorityQualifier : '—',
			},
			
			
			{
				title: 'Sites Using Standard',
				dataIndex: 'majorityCount',
				key: 'majorityCount',
				render: (_: number, record: BuildingBlockSummaryDTO) =>
					record.hasClearMajority ? record.majorityCount : '—',
			},
			
			{
				title: 'Coverage',
				dataIndex: 'percentage',
				key: 'coverage',
				render: (_: number, record: BuildingBlockSummaryDTO) =>
					record.hasClearMajority ? `${record.percentage}%` : '0%',
			},
			{
				title: 'Discrepancy',
				key: 'Discrepancy',
				render: (_: unknown, record: BuildingBlockSummaryDTO) =>
					record.hasClearMajority ? `${(100 - record.percentage).toFixed(1)}%` : '100%',
			},
		],
		[],
	);

	const buildingBlockSummaryData = useMemo(
		() => (details?.buildingBlockSummaries ?? []).slice(0, 5),
		[details?.buildingBlockSummaries],
	);

	return (
		<div className={styles.root}>
			<header className={styles.header}>
				<h1 className={styles.title}>Policy Analysis Details</h1>
				<Button onClick={onBack} type="default">
					Back
				</Button>
			</header>

			<section className={styles.content}>
				{loading && (
					<div className={styles.loadingState}>
					<Spin size="large" />
					</div>
				)}

				{!loading && (
					<>
						<p className={styles.summary}>{summaryText}</p>
						{meta.length > 0 && (
							<div className={styles.meta}>
								{meta.map((item) => (
									<div key={item.label} className={styles.metaItem}>
										<span className={styles.metaLabel}>{item.label}</span>
										<span className={styles.metaValue}>{item.value}</span>
									</div>
								))}
							</div>
						)}
					</>
				)}

				{!loading && downloadAvailable && details && (
					<div className={styles.downloadActions}>
						{details.downloadCsvUrl && (
							<Button
								icon={<DownloadOutlined />}
								onClick={() => downloadCsv(details.downloadCsvUrl!, details.csvFileName)}
								loading={csvDownloading}
							>
								Download CSV
							</Button>
						)}
						{details.downloadExcelUrl && (
							<Button
								icon={<DownloadOutlined />}
								onClick={() => downloadCsv(details.downloadExcelUrl!, details.excelFileName)}
								loading={csvDownloading}
							>
								Download Excel
							</Button>
						)}
					</div>
				)}

				{!loading && details && hasDiscrepancies && componentBlockSummary.length > 0 && (
					<div className={styles.tableSection}>
						<h2 className={styles.sectionTitle}>Component Block Summary</h2>
						{buildingBlockSummaryData.length > 0 && (
							<div className={styles.tableWrapper}>
								<Table<BuildingBlockSummaryDTO>
									columns={buildingBlockSummaryColumns}
									dataSource={buildingBlockSummaryData}
									rowKey={(record) => record.buildingBlockName}
									pagination={false}
									size="middle"
								/>
							</div>
						)}
						<div className={styles.tableWrapper}>
							<Table
								columns={summaryColumns}
								dataSource={componentBlockSummary}
								rowKey={(record) => record.componentBlock}
								pagination={false}
								size="middle"
							/>
						</div>
					</div>
				)}

				{!loading && details && (
					<div className={styles.tableSection}>
						<h2 className={styles.sectionTitle}>Discrepancies</h2>
						{hasDiscrepancies ? (
							<div className={styles.tableWrapper}>
								<Table<AnalysisDiscrepancyDTO>
									columns={discrepancyColumns}
									dataSource={sortedDiscrepancies}
									rowKey={(record) => `${record.buildingBlockName}-${record.impactedBlock}-${record.componentBuildingBlock}-${record.discrepancyComments}`}
									pagination={false}
									size="middle"
								/>
							</div>
						) : (
							<div className={styles.emptyState}>No discrepancies were reported for this analysis.</div>
						)}
					</div>
				)}

				{!loading && !analysis && (
					<div className={styles.emptyState}>Select a policy analysis from the list to view its details.</div>
				)}
			</section>
		</div>
	);
};

export default AnalysisDetails;
